import React, { useState, useEffect } from 'react';
import { categoryService } from '../../services/api';
import { Category, CategoryType, CategoryAccountType } from '../../types';
import CategoryForm from './CategoryForm';
import '../../styles/Categories.css';

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [filters, setFilters] = useState({
    type: '' as CategoryType | '',
    accountType: '' as CategoryAccountType | '',
  });
  const [showDeleted, setShowDeleted] = useState<boolean>(false);

  useEffect(() => {
    fetchCategories();
  }, [filters, showDeleted]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.type) params.type = filters.type;
      if (filters.accountType) params.accountType = filters.accountType;
      if (showDeleted) params.includeDeleted = showDeleted;

      const response = await categoryService.getAll(params);
      console.log('📋 Categories response:', response);
      const categoriesData = response.data?.data || response.data || [];
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateCategory = () => {
    console.log('Opening category modal');
    setEditingCategory(null);
    setShowModal(true);
    console.log('showModal state set to:', true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  const handleSaveCategory = async (categoryData: any) => {
    try {
      if (editingCategory) {
        await categoryService.update(editingCategory.id, categoryData);
      } else {
        await categoryService.create(categoryData);
      }
      fetchCategories();
      setShowModal(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category? This is a soft delete and can be restored.')) {
      return;
    }
    
    try {
      await categoryService.delete(id);
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleRestoreCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to restore this category?')) {
      return;
    }
    
    try {
      await categoryService.restore(id);
      fetchCategories();
    } catch (error) {
      console.error('Error restoring category:', error);
    }
  };

  const handlePermanentDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this category? This action cannot be undone!')) {
      return;
    }
    
    try {
      await categoryService.permanentDelete(id);
      fetchCategories();
    } catch (error) {
      console.error('Error permanently deleting category:', error);
    }
  };

  return (
    <div className="categories-page">
      <div className="categories-header">
        <h2>Categories Management</h2>
        <div className="header-actions">
          <label className="toggle-deleted">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            Show Deleted
          </label>
          <button className="btn btn-primary" onClick={handleCreateCategory}>
            <i className="fas fa-plus"></i> New Category
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <div className="filter-group">
          <label htmlFor="type">Filter by Type:</label>
          <select
            id="type"
            name="type"
            value={filters.type}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">All Types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="accountType">Filter by Account:</label>
          <select
            id="accountType"
            name="accountType"
            value={filters.accountType}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">All Accounts</option>
            <option value="personal">Personal</option>
            <option value="company">Company</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading categories...</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table categories-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Type</th>
                <th>Account Type</th>
                <th>Created At</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(categories) && categories.length > 0 ? (
                categories.map(category => (
                  <tr key={category.id} className={category.deleted_at ? 'deleted-row' : ''}>
                    <td>{category.id}</td>
                    <td>{category.name}</td>
                    <td>
                      <div className="description-cell">
                        {category.description || <span className="no-description">No description</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${category.type}`}>
                        {category.type}
                      </span>
                    </td>
                    <td>
                      <span className={`badge account-type ${category.account_type}`}>
                        {category.account_type}
                      </span>
                    </td>
                    <td>{new Date(category.created_at).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${category.deleted_at ? 'deleted' : 'active'}`}>
                        {category.deleted_at ? 'Deleted' : 'Active'}
                      </span>
                    </td>
                    <td>
                      <div className="category-actions">
                        {!category.deleted_at ? (
                          <>
                            <button
                              className="btn-icon edit"
                              onClick={() => handleEditCategory(category)}
                              title="Edit category"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn-icon delete"
                              onClick={() => handleDeleteCategory(category.id)}
                              title="Soft delete category"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn-icon restore"
                              onClick={() => handleRestoreCategory(category.id)}
                              title="Restore category"
                            >
                              <i className="fas fa-undo"></i>
                            </button>
                            <button
                              className="btn-icon permanent-delete"
                              onClick={() => handlePermanentDeleteCategory(category.id)}
                              title="Permanently delete category"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="no-categories">
                    {showDeleted ? 'No categories found (including deleted).' : 'No categories found. Add a new category to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Always render the CategoryForm but control visibility with CSS */}
      <div style={{ display: showModal ? 'block' : 'none' }}>
        <CategoryForm
          category={editingCategory}
          onSave={handleSaveCategory}
          onCancel={handleCloseModal}
        />
      </div>
    </div>
  );
};

export default Categories;
